import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { useAuthStore } from './store/authStore';
import { ConsoleProvider } from './contexts/ConsoleContext';

// Layouts
import TenantLayout from './components/layout/TenantLayout';
import PlatformPageHeader from './components/layout/PlatformPageHeader';
import AuthRedirect from './components/auth/AuthRedirect';

// Auth page (combined login/signup)
import AuthPage from './pages/AuthPage';
import KeepAgentPage from './pages/KeepAgentPage';
import WelcomePage from './pages/WelcomePage';
import InstallAppPage from './pages/InstallAppPage';
import NotFoundPage from './pages/NotFoundPage';

// Demo pages (public chatbot embeds)
import AgentDemoPage from './pages/AgentDemoPage';
import AgentDemoPagePreview from './pages/AgentDemoPage_Preview';

// Platform admin pages
import TenantManagementPage from './pages/TenantManagementPage';
import UserManagementPage from './pages/UserManagementPage';
import PromoteTenantPage from './pages/PromoteTenantPage';

// Tenant-scoped pages
import DashboardPage from './pages/DashboardPage';
import WorkspaceDashboardPage from './pages/WorkspaceDashboardPage';
import AgentConfigPage from './pages/AgentConfigPage';
import AgentManagementPage from './pages/AgentManagementPage';
import PromptPoolEditorPage from './pages/PromptPoolEditorPage';
import AnalyticsPage from './pages/AnalyticsPage';
import ImpactPage from './pages/ImpactPage';
import PlanUsagePage from './pages/PlanUsagePage';
import BillingPage from './pages/BillingPage';
import ConversationsPage from './pages/ConversationsPage';
import AILeadIntelligencePage from './pages/AILeadIntelligencePage';
import MissedOpportunitiesPage from './pages/MissedOpportunitiesPage';
import SupportPage from './pages/SupportPage';
import SupportInsightsPage from './pages/SupportInsightsPage';
import SupportNotificationsPage from './pages/SupportNotificationsPage';
import PlatformConfigurationPage from './pages/PlatformConfigurationPage';
import PromptEditorPage from './pages/PromptEditorPage';
import CRMConfigPage from './pages/CRMConfigPage';
import DMSConfigPage from './pages/DMSConfigPage';
import FinetunePage from './pages/FinetunePage';
import LicensesPage from './pages/LicensesPage';
import LicenseGeneratorPage from './pages/LicenseGeneratorPage';
import BillingConfig from './pages/platform/BillingConfig';
import PaymentProvidersPage from './pages/platform/PaymentProvidersPage';
import WebsiteFeedbackPage from './pages/platform/WebsiteFeedbackPage';
import SignupHealthPage from './pages/platform/SignupHealthPage';
import ConversationAuditPage from './pages/platform/ConversationAuditPage';
import RouteAnalytics from './components/common/RouteAnalytics';
import LicenseInfoPage from './pages/LicenseInfoPage';
import WorkflowStudioPage from './pages/WorkflowStudioPage';
import WorkflowManagementPage from './pages/WorkflowManagementPage';
import HealthCheckPage from './pages/HealthCheckPage';
import SmokeHistoryPage from './pages/SmokeHistoryPage';
import NotificationSettingsPage from './pages/NotificationSettingsPage';
import WhatsAppInboxPage from './pages/WhatsAppInboxPage';
import WhatsAppOutboundSettingsPage from './pages/WhatsAppOutboundSettingsPage';
import TenantGoLiveChecklistPage from './pages/TenantGoLiveChecklistPage';
import UserProfilePage from './pages/UserProfilePage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import FirstTimeSetupPage from './pages/FirstTimeSetupPage';
import SetupErrorBoundary from './components/onboarding/SetupErrorBoundary';

// RAG pages
import RAGManagementPage from './pages/RAGManagementPage';
import CollectionsPage from './pages/rag/Collections';
import DocumentsPage from './pages/rag/Documents';
import KnowledgeSourcesPage from './pages/rag/KnowledgeSources';
import UploadPage from './pages/rag/Upload';
import SearchPage from './pages/rag/Search';
import WebScraperPage from './pages/rag/WebScraper';
import CRMSyncPage from './pages/rag/CRMSync';
import DMSSyncPage from './pages/rag/DMSSync';
import DataCleanupPage from './pages/rag/DataCleanup';

// Create QueryClient
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30000,
    },
  },
});

/**
 * TenantRedirect - Redirects legacy routes to tenant-prefixed routes
 * Example: /rag/upload → /t/abc123/rag/upload
 */
function TenantRedirect() {
  const { isAuthenticated, user, isInitialized } = useAuthStore();
  const location = useLocation();

  // Wait for auth state to initialize
  if (!isInitialized) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!isAuthenticated || !user?.tenant_id) {
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }

  // Redirect to tenant-prefixed version of the current path
  const tenantPath = `/t/${user.tenant_id}${location.pathname}${location.search}`;
  return <Navigate to={tenantPath} replace />;
}

/**
 * RequireAuth - Protects routes that require authentication
 */
function RequireAuth({ children }) {
  const { isAuthenticated, isInitialized } = useAuthStore();
  const location = useLocation();

  // Wait for auth state to initialize before making decisions
  if (!isInitialized) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Double-check with getState() for immediate state access
  const currentState = useAuthStore.getState();
  if (!isAuthenticated && !currentState.isAuthenticated) {
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }

  return children;
}

/**
 * RequirePlatformAdmin - Protects platform admin routes
 */
function RequirePlatformAdmin({ children }) {
  const { isAuthenticated, user, isInitialized } = useAuthStore();
  const location = useLocation();

  // Wait for auth state to initialize before making decisions
  if (!isInitialized) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Double-check with getState() for immediate state access
  const currentState = useAuthStore.getState();
  const currentUser = currentState.user || user;
  const isAuth = isAuthenticated || currentState.isAuthenticated;

  if (!isAuth) {
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }

  // Check for superadmin access (including is_superuser flag)
  const isPlatformAdmin = currentUser?.is_superuser || 
    currentUser?.role === 'superadmin';

  if (!isPlatformAdmin) {
    return <Navigate to="/" replace />;
  }

  return children;
}

/**
 * RequireTenantAdmin - Protects tenant setup/configuration routes
 */
function RequireTenantAdmin({ children }) {
  const { isAuthenticated, user, isInitialized } = useAuthStore();
  const location = useLocation();

  if (!isInitialized) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const currentState = useAuthStore.getState();
  const currentUser = currentState.user || user;
  const isAuth = isAuthenticated || currentState.isAuthenticated;

  if (!isAuth) {
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }

  const isTenantAdmin =
    currentUser?.is_superuser ||
    currentUser?.role === 'superadmin' ||
    currentUser?.tenant_role === 'owner' ||
    currentUser?.tenant_role === 'admin' ||
    (currentUser?.role === 'admin' && currentUser?.tenant_id);

  if (!isTenantAdmin) {
    const tenantId = currentUser?.tenant_id;
    return <Navigate to={tenantId ? `/t/${tenantId}/dashboard` : '/welcome'} replace />;
  }

  return children;
}

/**
 * RequireSuperadmin - Protects one-time setup routes inside tenant scope.
 */
function RequireSuperadmin({ children }) {
  const { isAuthenticated, user, isInitialized } = useAuthStore();
  const location = useLocation();

  if (!isInitialized) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const currentState = useAuthStore.getState();
  const currentUser = currentState.user || user;
  const isAuth = isAuthenticated || currentState.isAuthenticated;

  if (!isAuth) {
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }

  const isSuperadmin = currentUser?.is_superuser || currentUser?.role === 'superadmin';
  if (!isSuperadmin) {
    const tenantId = currentUser?.tenant_id;
    return <Navigate to={tenantId ? `/t/${tenantId}/dashboard` : '/welcome'} replace />;
  }

  return children;
}

function App() {
  // Initialize auth from localStorage on app startup
  const initializeAuth = useAuthStore((state) => state.initializeAuth);
  
  React.useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  return (
    <QueryClientProvider client={queryClient}>
      <ConsoleProvider>
        <BrowserRouter>
          <RouteAnalytics />
          <Toaster position="top-right" />
          <Routes>
          {/* Public routes */}
          <Route path="/" element={<Navigate to="/auth/login" replace />} />
          <Route path="/welcome" element={<WelcomePage />} />
          {/* Public, signed in or not: serviti.ai's "Install app" link lands here. */}
          <Route path="/install" element={<InstallAppPage />} />
          <Route path="/keep-agent" element={<KeepAgentPage />} />
          
          {/* Auth routes */}
          <Route path="/auth" element={<AuthRedirect><AuthPage /></AuthRedirect>} />
          <Route path="/login" element={<Navigate to="/auth/login" replace />} />
          {/* /login has had a short alias since the start and /signup did not,
              so anyone told "go to app.serviti.ai and sign up" who types the
              obvious path landed on nothing. */}
          <Route path="/signup" element={<Navigate to="/auth/signup" replace />} />
          <Route path="/auth/login" element={<AuthRedirect><AuthPage /></AuthRedirect>} />
          <Route path="/auth/signup" element={<AuthRedirect><AuthPage /></AuthRedirect>} />
          <Route path="/auth/reset-password" element={<ResetPasswordPage />} />

          {/* Public demo routes - chatbot embeds */}
          <Route path="/demos/:agentName" element={<AgentDemoPage />} />
          {/* New cold/warm preview demo (cheaper — no scrape, sales-shareable link).
              /preview                            → generic cold-campaign default
              /preview?school=Greenwood            → URL-param personalized (warm)
              /preview?business=Acme&type=business → custom non-school
              /preview/:agentName                  → slug-based (real agent fetch) */}
          <Route path="/preview" element={<AgentDemoPagePreview />} />
          <Route path="/preview/:agentName" element={<AgentDemoPagePreview />} />
          
          {/* Legacy health URLs now route through platform-admin auth. The heavy
              checks behind this page are superadmin-gated at the API. */}
          <Route path="/health-check" element={<Navigate to="/platform/health-check" replace />} />
          <Route path="/health-check/smoke-history" element={<Navigate to="/platform/health-check/smoke-history" replace />} />

          {/* Platform admin routes */}
          <Route path="/platform" element={
            <RequirePlatformAdmin>
              <TenantLayout isPlatformAdmin />
            </RequirePlatformAdmin>
          }>
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<WorkspaceDashboardPage />} />
            <Route path="platform-status" element={<DashboardPage />} />
            <Route path="tenants" element={<TenantManagementPage />} />
            <Route path="users" element={
              <>
                <PlatformPageHeader tenantOptional />
                <UserManagementPage />
              </>
            } />
            <Route path="users/add" element={<UserManagementPage />} />
            <Route path="promote" element={<PromoteTenantPage />} />
            <Route path="license-generator" element={
              <>
                <PlatformPageHeader tenantOptional />
                <LicenseGeneratorPage />
              </>
            } />
            <Route path="billing" element={
              <>
                <PlatformPageHeader tenantOptional />
                <BillingConfig />
              </>
            } />
            <Route path="payment-providers" element={
              <>
                <PlatformPageHeader tenantOptional />
                <PaymentProvidersPage />
              </>
            } />
            <Route path="website-feedback" element={<WebsiteFeedbackPage />} />
            <Route path="signup-health" element={<RequireSuperadmin><SignupHealthPage /></RequireSuperadmin>} />
            <Route path="conversation-audit" element={<RequireSuperadmin><ConversationAuditPage /></RequireSuperadmin>} />

            {/* Additional platform routes - mirror tenant routes for superadmins */}
            <Route path="agents" element={
              <>
                <PlatformPageHeader />
                <AgentConfigPage />
              </>
            } />
            <Route path="agent-management" element={
              <>
                <PlatformPageHeader />
                <AgentManagementPage />
              </>
            } />
            <Route path="agent-management/:agentId/prompt-pool" element={<PromptPoolEditorPage />} />
            <Route path="workflow-studio" element={<WorkflowStudioPage />} />
            <Route path="workflow-management" element={<WorkflowManagementPage />} />
            <Route path="analytics" element={
              <>
                <PlatformPageHeader />
                <AnalyticsPage />
              </>
            } />
            <Route path="analytics/impact" element={
              <>
                <PlatformPageHeader />
                <ImpactPage />
              </>
            } />
            <Route path="plan-usage" element={
              <>
                <PlatformPageHeader />
                <PlanUsagePage />
              </>
            } />
            <Route path="conversations" element={
              <RequireTenantAdmin>
                <>
                  <PlatformPageHeader />
                  <ConversationsPage />
                </>
              </RequireTenantAdmin>
            } />
            <Route path="analytics/bot/*" element={<Navigate to="../analytics" replace />} />
            <Route path="analytics/chatbot/*" element={<Navigate to="../analytics" replace />} />
            <Route path="lead-intelligence" element={
              <>
                <PlatformPageHeader />
                <AILeadIntelligencePage />
              </>
            } />
            <Route path="missed-opportunities" element={
              <>
                <PlatformPageHeader />
                <MissedOpportunitiesPage />
              </>
            } />
            <Route path="support" element={
              <>
                <PlatformPageHeader />
                <SupportPage />
              </>
            } />
            <Route path="support-insights" element={
              <>
                <PlatformPageHeader />
                <SupportInsightsPage />
              </>
            } />
            <Route path="rag" element={<Navigate to="collections" replace />} />
            <Route path="rag/collections" element={<CollectionsPage />} />
            <Route path="rag/documents" element={<DocumentsPage />} />
            <Route path="rag/sources" element={<KnowledgeSourcesPage />} />
            <Route path="rag/upload" element={<UploadPage />} />
            <Route path="rag/search" element={<SearchPage />} />
            <Route path="rag/web-scraper" element={<WebScraperPage />} />
            <Route path="rag/crm-sync" element={<CRMSyncPage />} />
            <Route path="rag/dms-sync" element={<DMSSyncPage />} />
            <Route path="rag/data-management" element={<RAGManagementPage />} />
            <Route path="rag/data-cleanup" element={<DataCleanupPage />} />
            <Route path="configuration" element={<PlatformConfigurationPage />} />
            <Route path="prompt-editor" element={<PromptEditorPage />} />
            <Route path="crm-config" element={<CRMConfigPage />} />
            <Route path="dms-config" element={<DMSConfigPage />} />
            <Route path="fine-tune" element={<FinetunePage />} />
            <Route path="licenses" element={<LicensesPage />} />
            <Route path="licenses/usage" element={<LicensesPage />} />
            <Route path="settings" element={<PlatformConfigurationPage />} />
            <Route path="notification-settings" element={<NotificationSettingsPage />} />
            <Route path="whatsapp-outbound" element={<WhatsAppOutboundSettingsPage />} />
            <Route path="support-notifications" element={<SupportNotificationsPage />} />
            <Route path="whatsapp-inbox" element={<WhatsAppInboxPage />} />
            <Route path="go-live-checklist" element={<TenantGoLiveChecklistPage />} />
            <Route path="profile" element={<UserProfilePage />} />
            <Route path="health-check" element={<HealthCheckPage />} />
            <Route path="health-check/smoke-history" element={<SmokeHistoryPage />} />
          </Route>

          {/* Tenant-scoped routes - /t/:tenantId/* */}
          <Route path="/t/:tenantId" element={
            <RequireAuth>
              <TenantLayout />
            </RequireAuth>
          }>
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="get-started" element={<RequireTenantAdmin><SetupErrorBoundary><FirstTimeSetupPage /></SetupErrorBoundary></RequireTenantAdmin>} />
            <Route path="dashboard" element={<WorkspaceDashboardPage />} />
            <Route path="platform-status" element={<RequireSuperadmin><DashboardPage /></RequireSuperadmin>} />
            {/* Agents */}
            <Route path="agents" element={<RequireSuperadmin><AgentConfigPage /></RequireSuperadmin>} />
            <Route path="agent-management" element={<AgentManagementPage />} />
            <Route path="agent-management/:agentId/prompt-pool" element={<RequireTenantAdmin><PromptPoolEditorPage /></RequireTenantAdmin>} />

            {/* Workflows */}
            <Route path="workflow-studio" element={<RequireSuperadmin><WorkflowStudioPage /></RequireSuperadmin>} />
            <Route path="workflow-management" element={<RequireSuperadmin><WorkflowManagementPage /></RequireSuperadmin>} />
            
            {/* Analytics */}
            <Route path="analytics" element={<AnalyticsPage />} />
            <Route path="analytics/impact" element={<ImpactPage />} />
            <Route path="plan-usage" element={<PlanUsagePage />} />
            <Route path="billing" element={<RequireTenantAdmin><BillingPage /></RequireTenantAdmin>} />
            <Route path="conversations" element={<RequireTenantAdmin><ConversationsPage /></RequireTenantAdmin>} />
            <Route path="analytics/bot/*" element={<Navigate to="../analytics" replace />} />
            <Route path="analytics/chatbot/*" element={<Navigate to="../analytics" replace />} />
            <Route path="lead-intelligence" element={<AILeadIntelligencePage />} />
            <Route path="missed-opportunities" element={<MissedOpportunitiesPage />} />

            {/* Support */}
            <Route path="support" element={<SupportPage />} />
            <Route path="support-insights" element={<SupportInsightsPage />} />

            {/* RAG */}
            <Route path="rag" element={<Navigate to="sources" replace />} />
            <Route path="rag/collections" element={<RequireSuperadmin><CollectionsPage /></RequireSuperadmin>} />
            <Route path="rag/documents" element={<RequireSuperadmin><DocumentsPage /></RequireSuperadmin>} />
            <Route path="rag/sources" element={<KnowledgeSourcesPage />} />
            <Route path="rag/upload" element={<RequireTenantAdmin><UploadPage /></RequireTenantAdmin>} />
            <Route path="rag/search" element={<RequireSuperadmin><SearchPage /></RequireSuperadmin>} />
            <Route path="rag/web-scraper" element={<RequireTenantAdmin><WebScraperPage /></RequireTenantAdmin>} />
            <Route path="rag/crm-sync" element={<RequireSuperadmin><CRMSyncPage /></RequireSuperadmin>} />
            <Route path="rag/dms-sync" element={<RequireSuperadmin><DMSSyncPage /></RequireSuperadmin>} />
            <Route path="rag/data-management" element={<RequireSuperadmin><RAGManagementPage /></RequireSuperadmin>} />
            <Route path="rag/data-cleanup" element={<RequireSuperadmin><DataCleanupPage /></RequireSuperadmin>} />
            
            {/* Configuration */}
            <Route path="configuration" element={<RequireSuperadmin><PlatformConfigurationPage /></RequireSuperadmin>} />
            <Route path="prompt-editor" element={<RequireSuperadmin><PromptEditorPage /></RequireSuperadmin>} />
            <Route path="crm-config" element={<RequireSuperadmin><CRMConfigPage /></RequireSuperadmin>} />
            <Route path="dms-config" element={<RequireSuperadmin><DMSConfigPage /></RequireSuperadmin>} />
            <Route path="fine-tune" element={<RequireSuperadmin><FinetunePage /></RequireSuperadmin>} />
            
            {/* Users (tenant admin) */}
            <Route path="users" element={<RequireTenantAdmin><UserManagementPage /></RequireTenantAdmin>} />
            <Route path="user-management" element={<RequireTenantAdmin><UserManagementPage /></RequireTenantAdmin>} />
            <Route path="user-management/add" element={<RequireTenantAdmin><UserManagementPage /></RequireTenantAdmin>} />
            <Route path="user-management/profile" element={<RequireTenantAdmin><UserManagementPage /></RequireTenantAdmin>} />
            
            {/* Licenses */}
            <Route path="licenses" element={<RequireTenantAdmin><LicensesPage /></RequireTenantAdmin>} />
            <Route path="licenses/usage" element={<RequireTenantAdmin><LicensesPage /></RequireTenantAdmin>} />
            <Route path="license-info" element={<LicenseInfoPage />} />
            
            {/* Settings & Profile */}
            <Route path="settings" element={<RequireSuperadmin><PlatformConfigurationPage /></RequireSuperadmin>} />
            <Route path="notification-settings" element={<RequireTenantAdmin><NotificationSettingsPage /></RequireTenantAdmin>} />
            <Route path="whatsapp-outbound" element={<RequireTenantAdmin><WhatsAppOutboundSettingsPage /></RequireTenantAdmin>} />
            <Route path="support-notifications" element={<RequireTenantAdmin><SupportNotificationsPage /></RequireTenantAdmin>} />
            <Route path="whatsapp-inbox" element={<WhatsAppInboxPage />} />
            <Route path="go-live-checklist" element={<RequireTenantAdmin><TenantGoLiveChecklistPage /></RequireTenantAdmin>} />
            <Route path="profile" element={<RequireTenantAdmin><UserManagementPage /></RequireTenantAdmin>} />
            <Route path="my-profile" element={<UserProfilePage />} />
            
            {/* Health Check */}
            <Route path="health-check" element={<HealthCheckPage />} />
          </Route>

          {/* Legacy route redirects - redirect to tenant-prefixed versions */}
          <Route path="/dashboard" element={<TenantRedirect />} />
          <Route path="/agents/*" element={<TenantRedirect />} />
          <Route path="/agent-management" element={<TenantRedirect />} />
          <Route path="/workflow-studio" element={<TenantRedirect />} />
          <Route path="/workflow-management" element={<TenantRedirect />} />
          <Route path="/conversations/*" element={<TenantRedirect />} />
          <Route path="/analytics" element={<TenantRedirect />} />
          <Route path="/rag/*" element={<TenantRedirect />} />
          <Route path="/users/*" element={<TenantRedirect />} />
          <Route path="/user-management/*" element={<TenantRedirect />} />
          <Route path="/configuration" element={<TenantRedirect />} />
          <Route path="/crm-config" element={<TenantRedirect />} />
          <Route path="/dms-config" element={<TenantRedirect />} />
          <Route path="/licenses/*" element={<TenantRedirect />} />
          <Route path="/settings" element={<TenantRedirect />} />
          <Route path="/profile" element={<TenantRedirect />} />
          <Route path="/plan-usage" element={<TenantRedirect />} />
          <Route path="/billing" element={<TenantRedirect />} />

          {/* Public agent links live at the root, e.g. /Arkgroup-AI-Agent.
              Keep this after all known application routes so app URLs are not
              accidentally treated as agent slugs. */}
          <Route path="/:agentName" element={<AgentDemoPage />} />

          {/* 404 - catch all */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </BrowserRouter>
      </ConsoleProvider>
    </QueryClientProvider>
  );
}

export default App;
